/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The router's scroll manager. Mounted once in MainApp (App.tsx), outside the
 * route <Suspense>, so it sees every navigation.
 *
 * What it does, per navigation type:
 *
 * - PUSH / REPLACE to another pathname: an INSTANT jump to the top. The
 *   `html { scroll-behavior: smooth }` rule used to animate this jump too, so
 *   a new page slid up from wherever the previous one had been scrolled. The
 *   jump also tells the header (`scrollToNewPageTop`): a new page starts with
 *   it in view, and an anchor is measured against that.
 * - PUSH with a hash (a hero CTA, every "Vergelijken" link's `#start`, the
 *   trust chip to `/providers#dekking`): scroll to the anchor once it exists,
 *   placed just under the live header (lib/pageScroll.ts). The old version
 *   made one attempt after 50 ms with a hardcoded 100 px offset and gave up
 *   when the target was still being fetched — which on a phone is most of the
 *   time. A PUSH to the SAME url counts (React Router gives it a new key), so
 *   a CTA clicked twice scrolls twice, and the call sites no longer need their
 *   own scrollTo on top of the router's.
 * - REPLACE with a hash: the same as PUSH — React Router replaces instead of
 *   pushing when a link points at the current url, which is the hero CTA
 *   pressed twice. REPLACE without one (filters writing their state to the
 *   query, which drops any hash): nothing. Those are not navigations the
 *   visitor made, and the page must not move under a half-set filter.
 * - POP (back / forward): restore the position the visitor left that history
 *   entry at, waiting for the document to grow back to it. The browser's own
 *   restoration fires before React has rendered the page, which is why it is
 *   switched to manual here. A POP to a hash entry with no saved position
 *   scrolls to the anchor.
 * - `state.preserveScroll`: stay put (picking a province on /geen-laadpaal
 *   becomes a URL of its own while the visitor is mid-page).
 *
 * The first render is left alone on purpose: a shared `#id` link jumps
 * natively, and a smooth scroll-to-top on mount undid that. A reload or a
 * Back/Forward across documents does NOT come back to its position by itself:
 * `scrollRestoration = 'manual'` (below) is part of the history entry, so the
 * browser leaves the new document at its top (or at its `#id`). One case is
 * restored here: an entry whose view state (open rows, "Toon meer", the sort,
 * a search — src/lib/viewState.ts) the page restores after mount. Its saved
 * position belongs to that restored page, so it is put back once the page has
 * applied the restore and is tall enough — unless the visitor scrolls first. A
 * page without view state keeps the browser's placement, as before.
 *
 * Every location is also reported to the view-state store
 * (`noteViewStateLocation`): this component sees them all, first, which is
 * how the store knows when a document has left its first location.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  COMPARER_START_ID,
  resolveAnchorElement,
  scrollInstant,
  scrollToAnchor,
  scrollToNewPageTop,
} from '../lib/pageScroll';
import {
  firstLoadRestorePending,
  hasFirstLoadViewState,
  noteViewStateLocation,
} from '../lib/viewState';

const STORAGE_KEY = 'ere:scroll-positions';
const MAX_SAVED = 50;
const RESTORE_WAIT_MS = 2500;
/**
 * How long a first-load restore waits for the page to apply its view state
 * (the comparer's rows arrive after a fetch on a shell-served URL); the wait
 * for the height (RESTORE_WAIT_MS) starts after that.
 */
const VIEW_STATE_WAIT_MS = 8000;
/** Keys that scroll the page: pressed during the wait, the visitor is reading. */
const SCROLL_KEYS = new Set(['PageDown', 'PageUp', 'Home', 'End', 'ArrowDown', 'ArrowUp', ' ']);
// Timers, not animation frames, for both the recording throttle and the
// restore wait: a background or embedded tab throttles rAF to a standstill,
// and a position must still be recorded and restored there.
const RECORD_THROTTLE_MS = 100;
const RESTORE_POLL_MS = 50;

type Positions = Record<string, number>;

/**
 * A history entry's id in the position store: its key AND its address. The
 * first entry of EVERY document has the key "default", so in one tab a page
 * opened by a full load (a link out and back, an address typed) wrote its
 * position over the earlier document's — and a Back across documents onto
 * that earlier page read the wrong one (measured 2026-10-03: the comparer,
 * left at 6073, read the kennisbank's 0). Within a document a key is one
 * entry, and an entry's address never changes (a filter write is a new entry).
 */
const entryId = (key: string, pathname: string, search: string): string =>
  `${key}|${pathname}${search}`;

const readPositions = (): Positions => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Positions) : {};
  } catch {
    return {};
  }
};

const writePositions = (positions: Positions): void => {
  try {
    const keys = Object.keys(positions);
    if (keys.length > MAX_SAVED) {
      for (const k of keys.slice(0, keys.length - MAX_SAVED)) delete positions[k];
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    /* private mode / quota — restoration is a convenience, not a contract */
  }
};

const savePosition = (key: string, y: number): void => {
  const positions = readPositions();
  positions[key] = Math.round(y);
  writePositions(positions);
};

/**
 * Put a document loaded onto a remembered entry back at `saved`: first wait
 * until the page has applied its view state (the comparer's open rows and
 * "Toon meer", which make it taller), then until it is tall enough, then jump.
 * The first wheel, touch drag or scrolling key ends the wait — a jump under a
 * moving finger is worse than no restore. Returns the cancel.
 */
const restoreAfterViewState = (key: string, saved: number): (() => void) => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  const viewDeadline = Date.now() + VIEW_STATE_WAIT_MS;
  let heightDeadline: number | null = null;
  const onKey = (event: KeyboardEvent) => {
    if (SCROLL_KEYS.has(event.key)) stop();
  };
  const stop = () => {
    stopped = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchmove', stop);
    window.removeEventListener('keydown', onKey);
  };
  const attempt = () => {
    timer = null;
    if (stopped) return;
    if (firstLoadRestorePending(key)) {
      if (Date.now() >= viewDeadline) {
        stop();
        return;
      }
      timer = setTimeout(attempt, RESTORE_POLL_MS);
      return;
    }
    if (heightDeadline === null) heightDeadline = Date.now() + RESTORE_WAIT_MS;
    const maxTop = document.documentElement.scrollHeight - window.innerHeight;
    if (maxTop >= saved || Date.now() >= heightDeadline) {
      stop();
      scrollInstant(Math.min(saved, Math.max(0, maxTop)));
      return;
    }
    timer = setTimeout(attempt, RESTORE_POLL_MS);
  };
  window.addEventListener('wheel', stop, { passive: true });
  window.addEventListener('touchmove', stop, { passive: true });
  window.addEventListener('keydown', onKey);
  attempt();
  return stop;
};

export const ScrollToTop = () => {
  const { pathname, search, hash, state, key } = useLocation();
  const navigationType = useNavigationType();
  // A navigation may opt out of the jump-to-top: picking a province on
  // /geen-laadpaal makes the choice a URL of its own, but the visitor is
  // mid-page and should stay there.
  const preserveScroll = (state as { preserveScroll?: boolean } | null)?.preserveScroll === true;

  const prevRef = useRef<{ key: string; pathname: string; hash: string } | null>(null);
  // The location the page was opened at. A POP can bring it back later; the
  // first-location handling below must only apply while it is still current.
  const initialKeyRef = useRef(key);

  // Own the restoration: the browser's automatic one runs on popstate, before
  // the new page has rendered, and lands on whatever height the OLD page had.
  useEffect(() => {
    if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Remember where each history entry was left: on scroll (throttled), for the
  // entry the page is on, and once more when it is left — read in the commit
  // that leaves it, before the next page's DOM goes in.
  //
  // Until 2026-10-03 the listener belonged to its entry until its effect was
  // cleaned up, which runs AFTER the next page has been committed: the scroll
  // event the browser fires when it clamps the old position to a shorter new
  // page (a provider page in its loading state is one screen tall) reached the
  // old entry's listener, and the cleanup wrote that clamped value over the
  // real one (measured: 1660 → 36 px on desktop, 3858 → 812 px on a phone),
  // so Back restored the wrong place. Now scroll events between the commit
  // and this page's own location effect belong to no entry, and the position
  // read before the commit is the one saved.
  /** This location's id in the position store (`entryId`). */
  const entry = entryId(key, pathname, search);
  /** The entry scroll events are recorded for; null while a new page settles. */
  const activeEntryRef = useRef<string | null>(null);
  /** The entry just left and its position, read before the next page went in. */
  const leavingRef = useRef<{ entry: string; y: number } | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const record = () => {
      timer = null;
      const active = activeEntryRef.current;
      if (active !== null) savePosition(active, window.scrollY);
    };
    const onScroll = () => {
      if (timer) return;
      timer = setTimeout(record, RECORD_THROTTLE_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', record);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', record);
    };
  }, []);
  // A layout effect's cleanup runs in the commit, for this component before
  // the routes after it in the tree: the old page is still in the DOM, so
  // `scrollY` is where the visitor left it. Kept in a ref and saved by the
  // location effect below only when the key really changed — StrictMode's
  // simulated unmount runs this cleanup without leaving anything.
  useLayoutEffect(
    () => () => {
      if (activeEntryRef.current === entry) leavingRef.current = { entry, y: window.scrollY };
      activeEntryRef.current = null;
    },
    [entry]
  );

  useEffect(() => {
    // Before any page below has run its effects for this location.
    noteViewStateLocation(key, `${pathname}${search}`);
    const leaving = leavingRef.current;
    leavingRef.current = null;
    if (leaving && leaving.entry !== entry) savePosition(leaving.entry, leaving.y);
    activeEntryRef.current = entry;
    const prev = prevRef.current;
    prevRef.current = { key, pathname, hash };
    const id = hash.replace(/^#/, '');
    // First location: the browser already placed the document — a shared
    // `#id` link jumps natively; a reload or a Back across documents starts
    // at the top, `scrollRestoration` being manual (measured 2026-10-03 in
    // Chromium: 1500 → 0 and 1200 → 0), except for an entry with view state
    // (below). The one case it cannot handle is a hash whose element does not
    // exist yet: a deep link onto a page that renders its target after a fetch (the
    // comparer on a filtered URL, which is served as the SPA shell), where
    // the native jump found nothing and the visitor landed at the top. Wait
    // for that element; an element that is already there was jumped to
    // natively and is left alone — except the comparison's start (`#start`,
    // lib/pageScroll.ts), which no element carries as its id, so the browser
    // never moved for it: a CTA opened in a new tab, or the page reloaded
    // after one, still lands on the results on a phone. Unless the visitor
    // already scrolled the baked page before it hydrated: a native jump
    // never pulled a reader back either.
    //
    // `prev?.key === key` as well as `!prev`: in development <StrictMode>
    // runs this mount effect, its cleanup (which cancels the wait) and then
    // the effect again, with prevRef already set — the wait has to restart.
    const firstLocation = key === initialKeyRef.current && (!prev || prev.key === key);
    if (firstLocation) {
      // A reload (or a Back/Forward across documents) onto an entry whose
      // page restores its view state after mount: the position the visitor
      // left belongs to that restored page — it wins over the `#id`, which
      // only says where the block starts.
      if (hasFirstLoadViewState(key)) {
        const saved = readPositions()[entry];
        if (typeof saved === 'number') return restoreAfterViewState(key, saved);
      }
      if (id === COMPARER_START_ID) return window.scrollY > 0 ? undefined : scrollToAnchor(id);
      if (id && !resolveAnchorElement(id)) return scrollToAnchor(id);
      return;
    }
    if (!prev || prev.key === key) return;
    if (preserveScroll) return;

    const pathChanged = prev.pathname !== pathname;

    if (navigationType === 'POP') {
      const saved = readPositions()[entry];
      if (typeof saved === 'number') {
        // Wait for the restored page to be tall enough to hold the position.
        let timer: ReturnType<typeof setTimeout> | null = null;
        const deadline = Date.now() + RESTORE_WAIT_MS;
        const attempt = () => {
          timer = null;
          const maxTop = document.documentElement.scrollHeight - window.innerHeight;
          if (maxTop >= saved || Date.now() >= deadline) {
            scrollInstant(Math.min(saved, Math.max(0, maxTop)));
            return;
          }
          timer = setTimeout(attempt, RESTORE_POLL_MS);
        };
        attempt();
        return () => {
          if (timer) clearTimeout(timer);
        };
      }
      if (id) return scrollToAnchor(id);
      if (pathChanged) scrollToNewPageTop();
      return;
    }

    if (id) {
      // PUSH or REPLACE. REPLACE is included because React Router turns a
      // click on a link to the CURRENT url into a replace (useLinkClickHandler
      // defaults `replace` to "same path"), and that is exactly the hero CTA
      // pressed a second time — which must scroll again. A replace that only
      // rewrote the query never carries a hash (setSearchParams drops it), so
      // the filters stay out of this branch.
      //
      // A hash navigation onto another page: get the old page's scroll
      // position out of the way first, so the anchor wait does not leave the
      // visitor staring at the middle of the new page — and let the header
      // show itself first, as every new page starts with it in view: the
      // anchor below is measured against the offset it publishes. A plain
      // scrollInstant(0) left the old page's collapsed header in place until
      // the browser reported the top, which it did after the anchor had been
      // measured on a mouse click and not at all on a keyboard Enter.
      if (pathChanged) scrollToNewPageTop();
      return scrollToAnchor(id);
    }

    if (pathChanged) scrollToNewPageTop();
    // Same pathname, no hash: a filter wrote its state to the URL. Stay put.
  }, [key, entry, pathname, search, hash, navigationType, preserveScroll]);

  return null;
};
